import React, { useCallback, useMemo, useState } from 'react';
import { stanNotify } from '@/lib/stanNotify';
import { useTaxDocuments } from '../../queries/useTaxDocuments';
import { downloadTaxDocument, type TaxDocumentType } from '@/lib/api/settings';
import { handleApiError } from '@/lib/utils/apiErrorHandler';

/**
 * Get human-readable name for tax document type
 */
const getReadableName = (type: TaxDocumentType): string => {
    const typeMap: Record<TaxDocumentType, string> = {
        tax1099k: '1099-K',
        tax1099nec: '1099-NEC',
        expense_report: 'Expense Report',
        expense_report_nec: 'Expense Report NEC',
    };
    return typeMap[type] || type;
};

/**
 * Get file extension for tax document type
 */
const getFileExtension = (type: TaxDocumentType): string => {
    if (type === 'expense_report' || type === 'expense_report_nec') {
        return 'csv';
    }
    return 'pdf';
};

export const TaxDocumentsSection: React.FC = () => {
    const { data } = useTaxDocuments();
    const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);

    // Sort years in descending order
    const years = useMemo(() => {
        if (!data?.reports) return [];
        return Object.keys(data.reports).sort((a, b) => Number(b) - Number(a));
    }, [data?.reports]);

    const handleDownload = useCallback(async (year: string, type: TaxDocumentType) => {
        const docKey = `${year}-${type}`;
        setDownloadingDoc(docKey);

        try {
            const blob = await downloadTaxDocument(year, type);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `stripe-${year}-${getReadableName(type)}.${getFileExtension(type)}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            stanNotify({
                type: 'success',
                title: 'Success',
                text: 'Your download has started!',
            });
        } catch (error) {
            handleApiError(error, 'Something went wrong');
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: `We were unable to download your ${getReadableName(type)}. Please try again.`,
            });
        } finally {
            setDownloadingDoc(null);
        }
    }, []);

    // Don't render if no tax documents available
    if (years.length === 0) {
        return null;
    }

    return (
        <div className='max-w-5xl'>
            <div className='mb-2'>
                <h4>Tax Documents</h4>
            </div>
            <div className='grid grid-cols-2 gap-4 sm:flex sm:flex-wrap md:gap-8'>
                {years.map(year => (
                    <div key={year} className='min-w-0'>
                        <p className='text-caption-1-regular text-gray mb-1'>{year}</p>
                        <div className='flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:gap-3'>
                            {data?.reports[year]?.map(type => {
                                const docKey = `${year}-${type}`;
                                const isDownloading = downloadingDoc === docKey;

                                return (
                                    <button
                                        key={docKey}
                                        type='button'
                                        onClick={() => handleDownload(year, type)}
                                        disabled={isDownloading}
                                        className='text-label-sm-bold text-brand-500-main hover:text-primary-dark truncate disabled:opacity-50'
                                    >
                                        {isDownloading ? 'Downloading...' : getReadableName(type)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
